import cx from "classnames";

import styles from "./Dnd.module.scss";
import { CSSProperties } from "react";

/**
 * Props for the DndSortLine component
 * @extends React.ComponentPropsWithoutRef<"div">
 */
export type SortLineProps = React.ComponentPropsWithoutRef<"div"> & {
  /**
   * The offset of the line from the item. This is a CSS value, typically negative.
   */
  offset: string;
  /**
   * The orientation of the sort - Note this may be counterintuitive, as the line is rendered perpendicular to the sort.
   */
  orientation: "horizontal" | "vertical";
  /**
   * The position of the line relative to a target item.
   */
  position: "before" | "after";
};

/**
 * A visual indicator line that shows where an item will be dropped during drag and drop operations.
 *
 * Features:
 * - Visual feedback for drop positioning
 * - Configurable orientation (horizontal/vertical)
 * - Adjustable positioning (before/after)
 * - Customizable offset for precise placement
 * - Accessible with proper ARIA attributes
 * - Automatic CSS class management
 * - Supports all standard HTML div props
 *
 * @example
 * <DndSortLine
 *   offset="-10px"
 *   orientation="vertical"
 *   position="after"
 *   className="custom-drop-line"
 * />
 */
export const DndSortLine = ({
  offset,
  orientation,
  position,
  ...rest
}: SortLineProps) => {
  const classes = cx(
    styles["drop-line"],
    styles[`orientation-${orientation}`],
    styles[`position-${position}`],
  );

  return (
    <div
      aria-hidden
      className={classes}
      style={{ "--drop-line-offset": offset } as CSSProperties}
      {...rest}
    />
  );
};
